import React, { useEffect, useState, useRef } from 'react';
import Chart from 'chart.js/auto';
import moment from 'moment';

const MachineStatus = () => {
  const [currentValue, setCurrentValue] = useState(0);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    const updateStatusIndicators = async () => {
      try {
        const response = await fetch('https://cmti-edge.online/foundry/php/smartfoundry.php');
        const data = await response.json();
        const current = parseFloat(data.average_totalcurrent);
        setCurrentValue(current);
      } catch (error) {
        console.error('Error fetching current value:', error);
      }
    };

    updateStatusIndicators();
    const interval = setInterval(updateStatusIndicators, 2000);
    return () => clearInterval(interval);
  }, []);

  const updateGraph = async () => {
    try {
      const response = await fetch(`https://cmti-edge.online/foundry/php/curgraph.php?date=${selectedDate}`);
      const data = await response.json();

      const labels = [];
      const datasets = [
        { label: 'Printer (Em1_Current)', data: [], borderColor: 'yellow' },
        { label: 'Panel (Em2_Current)', data: [], borderColor: 'rgba(255, 99, 132, 1)' },
        { label: 'Furnace (Em3_Current)', data: [], borderColor: 'rgba(188, 26, 236, 1)' },
        { label: 'Mould (Em4_Current)', data: [], borderColor: '#33F5FF' }
      ];

      for (let minute = 0; minute < 24 * 60; minute++) {
        const time = moment(`${selectedDate}T00:00:00`).add(minute, 'minutes');
        if (time.isBetween(`${selectedDate}T${startTime}`, `${selectedDate}T${endTime}`, null, '[]')) {
          const dataObject = data.find(obj => {
            const createdTime = moment(obj.created_at);
            return Math.abs(time.diff(createdTime, 'minutes')) < 1;
          });

          labels.push(time.toISOString());
          datasets[0].data.push(dataObject ? parseFloat(dataObject.Em1_Current_Total) : 0);
          datasets[1].data.push(dataObject ? parseFloat(dataObject.Em2_Current_Total) : 0);
          datasets[2].data.push(dataObject ? parseFloat(dataObject.Em3_Current_Total) : 0);
          datasets[3].data.push(dataObject ? parseFloat(dataObject.Em4_Current_Total) : 0);
        }
      }

      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      chartInstance.current = new Chart(ctx, {
        type: 'line',
        data: { labels, datasets },
        options: {
          scales: {
            x: {
              type: 'time',
              time: {
                unit: 'minute',
                displayFormats: { minute: 'HH:mm' },
                unitStepSize: 30
              },
              title: { display: true, text: 'Time', color: 'white' },
              ticks: { color: 'orange', stepSize: 30 }
            },
            y: {
              title: { display: true, text: 'Current (A)', color: 'white' },
              ticks: { beginAtZero: true, color: 'orange' }
            }
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(2)}`
              }
            }
          },
          color: 'white'
        }
      });
    } catch (error) {
      console.error('Error updating graph:', error);
    }
  };

  useEffect(() => {
    updateGraph();
  }, []);

  const getStatusMessage = (value) => {
    if (value <= 0 || value < 5) {
      return "Not Running";
    } else if (value > 5 && value < 10) {
      return "Partially Available";
    } else if (value >= 10 && value < 12) {
      return "Machine is Running";
    } else if (value >= 12) {
      return "Heating Under Process";
    }
    return "";
  };

  return (
    <div className="p-5 text-white">
      <h2 className="mb-5 text-4xl text-orange-500">Machine Status</h2>
      
      <table className="w-full border-collapse bg-gray-800 shadow-md rounded-lg overflow-hidden mb-8">
  <thead className="bg-gray-700 text-white">
    <tr>
      <th className="py-4 px-6 text-center text-lg">Check On the Machine Availability</th>
      <th className="py-4 px-6 text-center text-lg">Machine Status</th>
    </tr>
  </thead>
  <tbody className="text-center  text-white">
    <tr className="border-b border-gray-600 ">
      <td className="py-4 px-6 flex justify-center items-center">
        <StatusIndicator 
          title="Machine Available" 
          color={currentValue > 10 ? 'green' : 'transparent'} 
        />
      </td>
      <td className="py-4 px-6">
        <span className="font-semibold text-lg">{getStatusMessage(currentValue)}</span>
      </td>
    </tr>
    <tr className="border-b border-gray-600">
      <td className="py-2 px-6 flex justify-center items-center">
        <StatusIndicator 
          title="Machine Partially Available" 
          color={currentValue > 5 && currentValue < 10 ? 'orange' : 'transparent'} 
        />
      </td>
      <td className="py-2 px-6 flex justify-center items-center"></td>
    </tr>
    <tr className="border-b border-gray-600 ">
      <td className="py-2 px-6 flex justify-center items-center">
        <StatusIndicator 
          title="Machine Not Available" 
          color={currentValue <= 5 ? 'red' : 'transparent'} 
        />
      </td>
      <td className="py-2 px-6 flex justify-center items-center"></td>
    </tr>
    <tr>
      <td className="py-2 px-6 flex justify-center items-center">
        <StatusIndicator 
          title="Heating Under Process" 
          color={currentValue >= 12 ? 'blue' : 'transparent'} 
        />
      </td>
      <td className="py-2 px-6"></td>
    </tr>
  </tbody>
</table>


      <h2 className="mb-5 text-2xl">Machine Status Graph</h2>
      <div className="mb-5 text-white p-2 rounded-md font-bold text-lg">
        <label className="mr-2">Select Date: </label>
        <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="mr-5 text-black w-52 p-2 rounded-md font-bold text-lg" />
        <label className="mr-2">Select Start Time: </label>
        <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mr-5 text-black w-52 p-2 rounded-md font-bold text-lg" />
        <label className="mr-2">Select End Time: </label>
        <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mr-5 text-black w-52 p-2 rounded-md font-bold text-lg" />
        <button onClick={updateGraph} className="bg-blue-500 text-white px-4 py-2 rounded">Update Graph</button>
      </div>
      <canvas ref={chartRef} width="600" height="200" className="w-full"></canvas>
    </div>
  );
};

const StatusIndicator = ({ title, color }) => (
  <div className="flex items-center mb-2">
    <div className={`w-5 h-5 rounded-full mr-2 ${color ? `bg-${color}-500` : ''}`}></div>
    <span className="font-bold">{title}</span>
  </div>
);

export default MachineStatus;
